import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import toast from "react-hot-toast";

import axiosInstant from "../../../api";
import { getPublicOrderPage } from "../../../api/endpoints";
import { DotLottleLoader } from "../../../components/Loader/DotLottleLoader";
import { PublicShareContainer } from "./components/PublicShareContainer";
import { ProjectPublicPanel } from "./components/ProjectPublicPanel";
import { FeedbackCriteriaDialog } from "../../videoProduction/components/videoReview/FeedbackCriteriaDialog";
import { BRAND_PROJECT_STATUSES } from "../../videoProduction/helper";
import { getBrandAllowedFileTypes } from "../../admin/videos/selectedVideoCardV2/helpers";
import { OrderRmnBrand } from "../../../types/rmn";
import { BriefAttachment } from "../../../components/Brief/constants";

export interface PublicProjectFileComment {
    id: number;
    guest_name: string;
    message: string;
    timestamp?: number;
    timestamp_end?: number;
    edited: boolean;
    created_at: string;
}

export interface PublicProjectFile {
    id: number;
    name: string;
    type: string;
    approved: boolean;
    downloadUrl: string;
    master: boolean;
    key?: string | null;
    location?: string;
    version?: number;
    // Needed by the download helpers to probe auto-transcribed srt/vtt and
    // iconik thumbnail paths.
    hash?: string | null;
    thumbnail_count?: number | null;
    review?: any;
    videoPros?: any[];
    videoProducts?: any[];
    comments?: PublicProjectFileComment[];
    content_channel_id?: number;
    brand_project_channel_id?: number | null;
    content_channel?: { id: number; name: string } | null;
}

// One entry per channel INSTANCE — a project may carry the same generic
// channel multiple times, distinguished by brand_project_channel_id.
export interface PublicProjectContentChannel {
    id: number;
    name: string;
    brand_project_channel_id?: number | null;
    project_option_selections?: any[];
}

export interface PublicProjectBrief {
    id: number;
    questions?: any;
    intakeForm?: any;
    attachments?: BriefAttachment[];
    denied_reason?: string;
}

// Full User model shape — matches private getBrandProjectById response
export interface PublicProjectPro {
    userid: number;
    displayName: string;
    avatar: string;
    professions: any[];
    projects?: any[];
    projectKeyPoints?: any[];
    [key: string]: any;
}

export interface PublicProject {
    id: number;
    title: string;
    description?: string;
    status: string;
    public_page_token?: string;
    brief?: PublicProjectBrief;
    pros?: PublicProjectPro[];
    projectFiles?: PublicProjectFile[];
    content_channels?: PublicProjectContentChannel[];
}

export interface PublicOrderResponse {
    id: number;
    name: string;
    description?: string;
    brand: {
        id: number;
        name: string;
        logo?: string;
        enabled_file_types?: string[];
    };
    rmn_brand?: OrderRmnBrand | null;
    projects: PublicProject[];
}

export default function OrderPublicPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [order, setOrder] = useState<PublicOrderResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [accessDenied, setAccessDenied] = useState(false);

    const fetchOrder = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            const response = await axiosInstant.get(getPublicOrderPage(token));
            setOrder(response.data);
        } catch (error: any) {
            if (error.response?.status === 403) {
                setAccessDenied(true);
            } else if (error.response?.status === 404) {
                navigate("/404");
            } else {
                toast.error("Failed to load project page");
                navigate("/404");
            }
        } finally {
            setLoading(false);
        }
    }, [token, navigate]);

    useEffect(() => {
        fetchOrder();
    }, [fetchOrder]);

    if (loading) {
        return (
            <PublicShareContainer title="Loading...">
                <Box className="flex justify-center items-center min-h-[400px]">
                    <DotLottleLoader />
                </Box>
            </PublicShareContainer>
        );
    }

    if (accessDenied) {
        return (
            <PublicShareContainer title="Page Not Available">
                <Box className="flex flex-col items-center justify-center py-16 gap-4">
                    <Typography variant="h6" color="textSecondary">
                        This page is not available.
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        The public page for this order has not been enabled.
                    </Typography>
                </Box>
            </PublicShareContainer>
        );
    }

    if (!order) return null;

    const descriptionContent = (
        <>
            {order.brand && (
                <Typography variant="body2" color="darkgray1">
                    Brand: {order.brand.name}
                </Typography>
            )}
        </>
    );

    return (
        <PublicShareContainer title={order.name} descriptionContent={descriptionContent}>
            <Box className="flex flex-col gap-6">
                {order.projects.length === 0 ? (
                    <Box className="flex justify-center items-center py-12">
                        <Typography variant="body1" color="textSecondary">
                            No projects available for this order.
                        </Typography>
                    </Box>
                ) : (
                    order.projects.map((project, index) => (
                        <ProjectPublicPanel
                            key={project.id}
                            // The brand (and its pre-approval setting) lives on
                            // the order here; the review header reads it from
                            // the project, as on the project share page.
                            project={(project as any).brand ? project : ({ ...project, brand: order.brand } as any)}
                            token={token!}
                            onRefresh={fetchOrder}
                            defaultExpanded={index === 0}
                            allowedFileTypes={getBrandAllowedFileTypes(
                                [order.rmn_brand, order.brand],
                            )}
                        />
                    ))
                )}
            </Box>
            <FeedbackCriteriaDialog
                autoShow={order.projects.some(
                    (p) => p.status === BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED
                )}
                seenKey={`feedbackCriteriaSeen_order_${token}`}
            />
        </PublicShareContainer>
    );
}
